import apiBase from './api-base';

const BASE = '/bookclub';

export interface BookClubStats {
  active_clubs: number;
  total_members: number;
  threads_this_week: number;
  books_finished_this_term: number;
  most_active_club: string | null;
}

export interface ClubCurrentBook {
  title: string;
  author: string;
  progress: number;
  updated_at: string;
}

export interface BookClub {
  id: number;
  name: string;
  slug: string;
  tag: string;
  advisor_name: string;
  description: string;
  schedule_text: string;
  location: string;
  tint: string;
  max_members: number | null;
  is_active: boolean;
  member_count: number;
  activity_level: 'high' | 'medium' | 'quiet';
  current_book: ClubCurrentBook | null;
  /** The requesting user's own membership. False/null for anonymous callers. */
  is_member: boolean;
  my_role: 'member' | 'leader' | null;
  created_at: string;
  updated_at: string;
}

export interface DiscussionThread {
  id: number;
  club_slug: string;
  title: string;
  author_name: string;
  is_pinned: boolean;
  reply_count: number;
  is_hot: boolean;
  last_reply_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeaturedVoice {
  id: number;
  quote: string;
  by: string;
  thread: string;
  club: string;
  week_of: string;
  year_label: string;
  is_active: boolean;
  reply_id: number;
}

export type RSVPStatus = 'attending' | 'not_attending';

export interface ClubMeeting {
  id: number;
  club_name: string;
  club_slug: string;
  date: string;
  start_time: string;
  end_time: string;
  topic: string;
  /** Null when nobody has RSVP'd yet: the count is an annotation subquery. */
  attending_count: number | null;
  location: string;
  /** The requesting user's own RSVP, null if they have not responded. */
  my_rsvp: RSVPStatus | null;
  created_at: string;
  updated_at: string;
}

interface PaginatedResponse<T> {
  results: T[];
  next: string | null;
  previous: string | null;
}

/** Offset-paginated lists (members, reading list, reports, flags): the standard envelope. */
export interface OffsetPaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const bookclubStatsApi = {
  getStats: (): Promise<BookClubStats> =>
    apiBase.get<BookClubStats>(`${BASE}/stats/`).then((r) => r.data),
};

export const bookclubClubsApi = {
  getClubs: (params?: {
    tag?: string;
    search?: string;
    ordering?: string;
    page_size?: number;
  }): Promise<PaginatedResponse<BookClub>> =>
    apiBase
      .get<PaginatedResponse<BookClub>>(`${BASE}/clubs/`, { params })
      .then((r) => r.data),

  getClub: (slug: string): Promise<BookClub> =>
    apiBase.get<BookClub>(`${BASE}/clubs/${slug}/`).then((r) => r.data),

  getMembers: (
    slug: string,
    role?: 'member' | 'leader',
    params?: { page?: number; page_size?: number }
  ): Promise<OffsetPaginatedResponse<ClubMembership>> =>
    apiBase
      .get<OffsetPaginatedResponse<ClubMembership>>(`${BASE}/clubs/${slug}/members/`, {
        params: { ...(role ? { role } : {}), ...(params ?? {}) },
      })
      .then((r) => r.data),
};

export interface DiscussionReply {
  id: number;
  author_id: number;
  author_name: string;
  content: string;
  /** Set when the author revised it, so edits are never silent. */
  edited_at: string | null;
  created_at: string;
}

export interface DiscussionThreadDetail extends DiscussionThread {
  replies: DiscussionReply[];
}

/** The API soft-deletes flagged content and answers 201 with this marker
 *  rather than an error, so the caller can tell the author it is held. */
export type ModeratedResponse<T> = T & { warning?: 'CONTENT_FLAGGED' };

export const bookclubDiscussionsApi = {
  getDiscussions: (params?: {
    club?: string;
    ordering?: string;
    is_hot?: boolean;
    page_size?: number;
  }): Promise<PaginatedResponse<DiscussionThread>> =>
    apiBase
      .get<PaginatedResponse<DiscussionThread>>(`${BASE}/discussions/`, { params })
      .then((r) => r.data),

  getThread: (id: number): Promise<DiscussionThreadDetail> =>
    apiBase.get<DiscussionThreadDetail>(`${BASE}/discussions/${id}/`).then((r) => r.data),

  /** Club membership is enforced server-side; non-members get 403. */
  createThread: (club: number, title: string): Promise<ModeratedResponse<DiscussionThread>> =>
    apiBase
      .post<ModeratedResponse<DiscussionThread>>(`${BASE}/discussions/`, { club, title })
      .then((r) => r.data),

  createReply: (
    threadId: number,
    content: string,
  ): Promise<ModeratedResponse<DiscussionReply>> =>
    apiBase
      .post<ModeratedResponse<DiscussionReply>>(`${BASE}/discussions/${threadId}/replies/`, {
        content,
      })
      .then((r) => r.data),

  /* The trailing slash is required: without it Django's APPEND_SLASH answers
     301 and the redirect drops the PATCH body. */
  editReply: (
    threadId: number,
    replyId: number,
    content: string,
  ): Promise<ModeratedResponse<DiscussionReply>> =>
    apiBase
      .patch<ModeratedResponse<DiscussionReply>>(
        `${BASE}/discussions/${threadId}/replies/${replyId}/`,
        { content },
      )
      .then((r) => r.data),

  /** Authors withdraw their own; librarians may remove any. */
  deleteReply: (threadId: number, replyId: number): Promise<void> =>
    apiBase
      .delete(`${BASE}/discussions/${threadId}/replies/${replyId}/`)
      .then(() => undefined),
};

export const bookclubFeaturedVoiceApi = {
  getFeaturedVoice: (): Promise<FeaturedVoice | null> =>
    apiBase.get<FeaturedVoice | null>(`${BASE}/featured-voice/`).then((r) => r.data),
};

export const bookclubMeetingsApi = {
  getMeetings: (params?: {
    days?: number;
    club?: string;
    page_size?: number;
  }): Promise<PaginatedResponse<ClubMeeting>> =>
    apiBase
      .get<PaginatedResponse<ClubMeeting>>(`${BASE}/meetings/`, { params })
      .then((r) => r.data),

  rsvp: (id: number, status: RSVPStatus): Promise<{ status: RSVPStatus }> =>
    apiBase
      .post<{ status: RSVPStatus }>(`${BASE}/meetings/${id}/rsvp/`, { status })
      .then((r) => r.data),
};

export interface ClubMembership {
  id: number;
  user_id: number;
  full_name: string;
  role: 'member' | 'leader';
  joined_at: string;
}

export interface ProposalData {
  name: string;
  tag: string;
  advisor_name: string;
  first_read: string;
  description: string;
}

export interface ClubProposal {
  id: number;
  name: string;
  tag: string;
  advisor_name: string;
  first_read: string;
  description: string;
  proposer_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export const bookclubMembershipApi = {
  joinClub: (slug: string): Promise<ClubMembership> =>
    apiBase.post<ClubMembership>(`${BASE}/clubs/${slug}/join/`).then((r) => r.data),

  leaveClub: (slug: string): Promise<void> =>
    apiBase.post(`${BASE}/clubs/${slug}/leave/`).then(() => undefined),
};

export const bookclubProposalsApi = {
  submitProposal: (data: ProposalData): Promise<ClubProposal> =>
    apiBase.post<ClubProposal>(`${BASE}/proposals/`, data).then((r) => r.data),

  getProposals: (status?: string): Promise<PaginatedResponse<ClubProposal>> =>
    apiBase
      .get<PaginatedResponse<ClubProposal>>(`${BASE}/proposals/`, { params: status ? { status } : undefined })
      .then((r) => r.data),

  approveProposal: (id: number): Promise<ClubProposal> =>
    apiBase.post<ClubProposal>(`${BASE}/proposals/${id}/approve/`).then((r) => r.data),

  rejectProposal: (id: number): Promise<ClubProposal> =>
    apiBase.post<ClubProposal>(`${BASE}/proposals/${id}/reject/`).then((r) => r.data),

  convertProposal: (id: number): Promise<BookClub> =>
    apiBase.post<BookClub>(`${BASE}/proposals/${id}/convert/`).then((r) => r.data),
};

export interface ModerationDashboard {
  pending_reports: number;
  unresolved_flags: number;
  actions_today: number;
  flagged_users: number;
}

export interface ContentReport {
  id: number;
  reporter_name: string;
  content_type: number;
  object_id: number;
  reason: string;
  detail: string;
  status: 'pending' | 'reviewed' | 'actioned' | 'dismissed';
  created_at: string;
}

export const bookclubLibrarianApi = {
  createClub: (data: Partial<BookClub>): Promise<BookClub> =>
    apiBase.post<BookClub>(`${BASE}/clubs/`, data).then((r) => r.data),

  archiveClub: (slug: string): Promise<void> =>
    apiBase.post(`${BASE}/clubs/${slug}/archive/`).then(() => undefined),

  deleteThread: (id: number): Promise<void> =>
    apiBase.delete(`${BASE}/discussions/${id}/`).then(() => undefined),

  getModerationDashboard: (): Promise<ModerationDashboard> =>
    apiBase.get<ModerationDashboard>(`${BASE}/moderation/dashboard/`).then((r) => r.data),

  getReports: (status?: string): Promise<PaginatedResponse<ContentReport>> =>
    apiBase
      .get<PaginatedResponse<ContentReport>>(`${BASE}/reports/`, {
        params: status ? { status } : undefined,
      })
      .then((r) => r.data),

  reviewReport: (id: number, action: string, resolution_note?: string): Promise<void> =>
    apiBase
      .post(`${BASE}/reports/${id}/review/`, { action, resolution_note })
      .then(() => undefined),
};
